import { price as priceUtils } from '@eti/utils';
import type { AncillaryBundleOnTdPageFragmentType } from '../common/types/fragments/models';
import type {
  AncillaryBundleOptionsFeatureType,
  AncillaryBundleOptionType,
} from '../common/utils/models';

export const transformBundleData = (bundleData: AncillaryBundleOnTdPageFragmentType | null) => {
  if (!bundleData || !bundleData.options) {
    return null;
  }

  const options = bundleData.options
    .map((option) => {
      if (option) {
        const { name, price, originalPrice, tier, content, ...restOptions } = option;
        return {
          ...restOptions,
          priceRaw: price?.price?.value,
          originalPrice: originalPrice?.price?.value,
          title: name,
          type: tier,
          features: (content || [])
            .map((contentEntry) => {
              if (contentEntry) {
                const { included, ...restContent } = contentEntry;
                return {
                  ...restContent,
                  isIncluded: included,
                };
              }

              return null;
            })
            .filter((feature): feature is AncillaryBundleOptionsFeatureType => feature !== null),
        };
      }
      return null;
    })
    .filter((option): option is AncillaryBundleOptionType => option !== null);

  return { ...bundleData, options };
};

export type TransformBundleData = ReturnType<typeof transformBundleData>;

export const getPriorities = (recommendedPriority: number) => ({
  BASIC: 0,
  RECOMMENDED: recommendedPriority,
  PREMIUM: recommendedPriority === 1 ? 2 : 1,
  DELUXE: 3,
});

const prioritiesMobile = {
  RECOMMENDED: 0,
  DELUXE: 1,
  PREMIUM: 2,
  BASIC: 3,
};

// TODO. Handle sort bundle options in transformBundleData to avoid sort in other places.
export const sortOptions = (options: AncillaryBundleOptionType[] = []) => {
  const recommendedPriority = Math.max(options?.length - 2, 1);
  const priorities = getPriorities(recommendedPriority);

  return [...options].sort((a, b) => priorities[a.type] - priorities[b.type]);
};

export const mobileBundleOptionOrder = (sellableOptions: AncillaryBundleOptionType[]) =>
  [...sellableOptions].sort((a, b) => prioritiesMobile[a.type] - prioritiesMobile[b.type]);

export const saveUpToAmountWithBundle = (
  options: AncillaryBundleOptionType[],
  percentage: number,
) => {
  const optionWithHighestPrice = options.reduce((max, item) =>
    item.originalPrice > max.originalPrice ? item : max,
  );

  const savedAmount = (optionWithHighestPrice.originalPrice * percentage) / 100;
  return priceUtils.formatPrice(savedAmount);
};

export const uniqueFeatures = (
  sellableOptions: AncillaryBundleOptionType[],
): AncillaryBundleOptionsFeatureType[] =>
  sellableOptions.reduce((acc: AncillaryBundleOptionsFeatureType[], option) => {
    option.features.forEach((feature: AncillaryBundleOptionsFeatureType) => {
      if (!acc.some((item) => item.name === feature.name)) {
        acc.push(feature);
      }
    });
    return acc;
  }, []);
